import { createContext, useContext, useEffect, useMemo } from 'react';

import { PAGE_SIZE_DEFAULT } from 'bundles/unified-home-common/constants/MyLearningConstants';

export type PageSizeContextType = {
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  currentPageNum: number;
  onPageChange: (value: number) => void;
};

const PageSizeContext = createContext<PageSizeContextType>({
  pageSize: PAGE_SIZE_DEFAULT,
  onPageSizeChange: () => undefined,
  onPageChange: () => undefined,
  currentPageNum: 1,
});

type ClampOptions = {
  totalProducts: number;
  showPagination: boolean;
};

/**
 * Access the page size context.
 *
 * Pass `options` only when driving a CDS Pagination component. The My Learning page shares
 * one `?page=` query param across all tabs, so switching to a tab with fewer items can leave
 * `currentPageNum` past the last valid page — which crashes CDS Pagination. When `options`
 * is provided, `currentPageNum` is clamped to the valid range and the URL is rewritten so
 * the tab loads on a real page.
 */
export function usePageSize(options?: ClampOptions) {
  const context = useContext(PageSizeContext);
  const { pageSize, currentPageNum, onPageChange } = context;
  const totalProducts = options?.totalProducts;
  const showPagination = options?.showPagination ?? false;
  const shouldClamp = options !== undefined;

  // When `options` is omitted, return the URL page as-is so non-Pagination callers
  // (skill providers, enrolled-products providers, etc.) get unchanged behavior.
  const clampedPageNum = useMemo(() => {
    if (!shouldClamp || totalProducts === undefined) {
      return currentPageNum;
    }
    const safePageSize = pageSize > 0 ? pageSize : 1;
    // Empty list: keep the URL page (>= 1) so we don't fight a loading -> 0 -> N transition.
    if (totalProducts <= 0) {
      return Math.max(1, currentPageNum);
    }
    const totalPages = Math.max(1, Math.ceil(totalProducts / safePageSize));
    return Math.min(Math.max(1, currentPageNum), totalPages);
  }, [shouldClamp, totalProducts, currentPageNum, pageSize]);

  // Sync the URL only when the Pagination is actually visible. `showPagination` is false
  // while products are loading, which prevents an out-of-range URL from being rewritten
  // before the real total arrives.
  useEffect(() => {
    if (!shouldClamp || !showPagination || currentPageNum === clampedPageNum) {
      return;
    }
    onPageChange(clampedPageNum);
  }, [shouldClamp, showPagination, currentPageNum, clampedPageNum, onPageChange]);

  return { ...context, currentPageNum: clampedPageNum };
}

export default PageSizeContext;
